import {
  type AttachmentUploadCycleResult,
  createAttachmentEnvironmentAtoms,
  deletePendingAttachmentUpload,
  runAttachmentUploadCycle,
} from "@afar-research/client-runtime/state/attachments";
import { resolveAssetUrl } from "@afar-research/client-runtime/state/assets";
import type { AttachmentCreateUploadUrlInput, EnvironmentId } from "@afar-research/contracts";

import { connectionAtomRuntime } from "../connection/runtime";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { readPreparedConnection } from "./session";

export const attachmentEnvironment = createAttachmentEnvironmentAtoms(connectionAtomRuntime);

/**
 * Sends one body through the Environment's signed attachment upload and leaves
 * it pending, for whatever claims it next: a turn, or a browser recording. A
 * failed attempt that already minted an id deletes its pending upload.
 */
export async function uploadPendingAttachment(input: {
  readonly environmentId: EnvironmentId;
  readonly upload: AttachmentCreateUploadUrlInput;
  readonly body: Blob;
  readonly signal?: AbortSignal;
}): Promise<AttachmentUploadCycleResult> {
  const result = await runAttachmentUploadCycle({
    registry: appAtomRegistry,
    createUploadUrl: attachmentEnvironment.createUploadUrl,
    remove: attachmentEnvironment.remove,
    environmentId: input.environmentId,
    upload: input.upload,
    resolveUploadUrl: (relativeUrl) => {
      const connection = readPreparedConnection(input.environmentId);
      return connection ? resolveAssetUrl(connection.httpBaseUrl, relativeUrl) : null;
    },
    transport: (url) => {
      const controller = new AbortController();
      return {
        abort: () => controller.abort(),
        done: fetch(url, {
          method: "POST",
          headers: { "Content-Type": input.upload.mimeType },
          body: input.body,
          signal: input.signal
            ? AbortSignal.any([controller.signal, input.signal])
            : controller.signal,
        }).then((response) => {
          if (!response.ok) throw new Error(`Attachment upload rejected (${response.status}).`);
        }),
      };
    },
  });
  if (result.status !== "uploaded" && result.attachmentId) {
    deletePendingAttachmentUpload({
      registry: appAtomRegistry,
      remove: attachmentEnvironment.remove,
      environmentId: input.environmentId,
      attachmentId: result.attachmentId,
    });
  }
  return result;
}

/** Uploads a composer file ahead of the send; resolves to the pending upload's id. */
export async function uploadComposerAttachmentFile(
  environmentId: EnvironmentId,
  file: File,
): Promise<string> {
  return uploadFileAhead(environmentId, file, "file");
}

/**
 * Uploads one file of a Project Reference ahead of creation. A reference has
 * no size ceiling: it lands in `references/` and is read by path.
 */
export async function uploadProjectReferenceFile(
  environmentId: EnvironmentId,
  file: File,
): Promise<string> {
  return uploadFileAhead(environmentId, file, "reference");
}

async function uploadFileAhead(
  environmentId: EnvironmentId,
  file: File,
  type: "file" | "reference",
): Promise<string> {
  const result = await uploadPendingAttachment({
    environmentId,
    upload: {
      type,
      name: file.name,
      mimeType: file.type || "application/octet-stream",
      sizeBytes: file.size,
    },
    body: file,
  });
  if (result.status === "uploaded") return result.attachmentId;
  throw new Error(`Could not upload '${file.name}'.`, {
    cause: result.status === "failed" ? result.error : undefined,
  });
}
